import React, { useRef, useState } from "react";
// Helper to list S3 files (public bucket, CORS required)
async function listS3Files(): Promise<string[]> {
   // This requires the bucket to expose a public XML listing (not default for S3)
   const url = "https://mapit2-pictures.s3.eu-central-1.amazonaws.com/mapitNewFeaturesImages/";
   const res = await fetch(url);
   if (!res.ok) throw new Error("Failed to fetch S3 file list");
   const xml = await res.text();
   // Parse XML for <Key> elements
   const keys = Array.from(xml.matchAll(/<Key>(.*?)<\/Key>/g)).map(m => m[1]);
   return keys.filter(k => k && !k.endsWith('/'));
}

// Helper to delete a file from S3 (requires public DELETE or CORS-enabled API Gateway proxy)
async function deleteS3File(key: string): Promise<void> {
   // This will only work if the bucket allows public DELETE (not default, not recommended for production)
   // In production, use a backend API to sign and proxy the delete request securely.
   const url = `https://mapit2-pictures.s3.eu-central-1.amazonaws.com/${key}`;
   const res = await fetch(url, { method: "DELETE" });
   if (!res.ok) throw new Error("Delete failed: " + res.statusText);
}


export interface S3ImageUploadPopupProps {
   onClose: () => void;
   onUploadSuccess?: (url: string) => void;
}

export const S3ImageUploadPopup: React.FC<S3ImageUploadPopupProps> = ({ onClose, onUploadSuccess }) => {
   const [deletingKey, setDeletingKey] = useState<string | null>(null);
   const handleDeleteFile = async (key: string) => {
      if (!window.confirm(`Slet filen '${key.replace('mapitNewFeaturesImages/', '')}'?`)) return;
      setDeletingKey(key);
      setListError(null);
      try {
         await deleteS3File(key);
         setFileList((prev) => prev.filter(f => f !== key));
      } catch (err: any) {
         setListError(err.message || "Unknown error");
      } finally {
         setDeletingKey(null);
      }
   };
   const [showList, setShowList] = useState(false);
   const [fileList, setFileList] = useState<string[]>([]);
   const [listError, setListError] = useState<string | null>(null);
   const handleShowList = async () => {
      setListError(null);
      setShowList(true);
      try {
         const files = await listS3Files();
         setFileList(files);
      } catch (err: any) {
         setListError(err.message || "Unknown error");
      }
   };
   const fileInputRef = useRef<HTMLInputElement>(null);
   const [uploading, setUploading] = useState(false);
   const [error, setError] = useState<string | null>(null);
   const [successUrl, setSuccessUrl] = useState<string | null>(null);

   const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
      setError(null);
      const file = e.target.files?.[0];
      if (!file) return;
      setUploading(true);
      try {
         // Get a presigned URL from your backend (not implemented here)
         // For demo, upload directly (public bucket, CORS enabled)
         // Upload to subdirectory 'mapitNewFeaturesImages'
         const s3Key = `mapitNewFeaturesImages/${encodeURIComponent(file.name)}`;
         const s3Url = `https://mapit2-pictures.s3.eu-central-1.amazonaws.com/${s3Key}`;
         // Try to set public-read ACL (works only if bucket policy allows it)
         const res = await fetch(s3Url /*+ '?x-amz-acl=public-read'*/, {
            method: "PUT",
            body: file,
            headers: {
               "Content-Type": file.type,
               // "x-amz-acl": "public-read"
            },
         });
         if (!res.ok) throw new Error("Upload failed: " + res.statusText);
         setSuccessUrl(s3Url);
         if (onUploadSuccess) onUploadSuccess(s3Url);
      } catch (err: any) {
         setError(err.message || "Unknown error");
      } finally {
         setUploading(false);
      }
   };

   return (
      <div style={{ position: "fixed", top: 0, left: 0, width: "100vw", height: "100vh", background: "rgba(0,0,0,0.3)", zIndex: 9999, display: "flex", alignItems: "center", justifyContent: "center" }}>
         <div style={{ background: "white", padding: 24, borderRadius: 8, minWidth: 320, boxShadow: "0 2px 16px #0002" }}>
            <h3>Upload billede til S3</h3>
            <input ref={fileInputRef} type="file" accept="image/*" onChange={handleFileChange} disabled={uploading} />
            {uploading && <div>Uploader...</div>}
            {error && <div style={{ color: "red" }}>{error}</div>}
            {successUrl && (
               <div style={{ color: "green" }}>
                  Uploadet! <a href={successUrl} target="_blank" rel="noopener noreferrer">Se billede</a>
                  <div style={{ marginTop: 8 }}>
                     <input style={{ width: "90%" }} value={successUrl} readOnly />
                     <button style={{ marginLeft: 4 }} onClick={() => {
                        navigator.clipboard.writeText(successUrl);
                     }}>Kopier sti</button>
                  </div>
               </div>
            )}
            <div style={{ marginTop: 16, textAlign: "right" }}>
               <button onClick={onClose} disabled={uploading}>Luk</button>
               <button style={{ marginLeft: 8 }} onClick={handleShowList} disabled={uploading}>Vis liste af billeder</button>
            </div>
            {showList && (
               <div style={{ marginTop: 16, background: "#f9f9f9", borderRadius: 8, padding: 12, maxHeight: 300, overflowY: "auto" }}>
                  <h4>Billeder i mapitNewFeaturesImages</h4>
                  {listError && <div style={{ color: "red" }}>{listError}</div>}
                  {fileList.length === 0 && !listError && <div>Ingen billeder fundet.</div>}
                  <ul style={{ paddingLeft: 16 }}>
                     {fileList.map((file, idx) => (
                        <li key={idx} style={{ display: "flex", alignItems: "center", gap: 8 }}>
                           <a href={`https://mapit2-pictures.s3.eu-central-1.amazonaws.com/${file}`} target="_blank" rel="noopener noreferrer">{file.replace('mapitNewFeaturesImages/', '')}</a>
                           <button
                              style={{ fontSize: 12, color: "#a00", border: "1px solid #a00", background: "#fff", borderRadius: 4, padding: "2px 6px", cursor: "pointer" }}
                              disabled={deletingKey === file}
                              onClick={() => handleDeleteFile(file)}
                           >
                              {deletingKey === file ? "Sletter..." : "Slet"}
                           </button>
                        </li>
                     ))}
                  </ul>
                  <div style={{ textAlign: "right" }}>
                     <button onClick={() => setShowList(false)}>Luk liste</button>
                  </div>
               </div>
            )}
         </div>
      </div>
   );
};
